// Hook for loading and saving incident email notification settings
// CREATED: Wraps getIncidentEmailSettings and updateIncidentEmailSettings with loading/error state

import { useState, useEffect, useCallback } from 'react';
import {
  getIncidentEmailSettings,
  updateIncidentEmailSettings,
  type IncidentEmailSetting,
  type IncidentEmailSettingUpdate,
} from '../lib/incidentSettings';

interface UseIncidentEmailSettingsReturn {
  settings: IncidentEmailSetting[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  saveError: string | null;
  reload: () => void;
  save: (updates: IncidentEmailSettingUpdate[]) => Promise<boolean>;
}

export function useIncidentEmailSettings(isOpen: boolean): UseIncidentEmailSettingsReturn {
  const [settings, setSettings] = useState<IncidentEmailSetting[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const result = await getIncidentEmailSettings();

    if (result.success && result.data) {
      setSettings(result.data);
    } else {
      setError(result.error || 'Erreur de chargement');
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      load();
    }
  }, [isOpen, load]);

  const save = useCallback(async (updates: IncidentEmailSettingUpdate[]): Promise<boolean> => {
    setIsSaving(true);
    setSaveError(null);

    const result = await updateIncidentEmailSettings(updates);

    setIsSaving(false);

    if (!result.success) {
      setSaveError(result.error || 'Erreur lors de l\'enregistrement');
      return false;
    }

    await load();
    return true;
  }, [load]);

  return {
    settings,
    isLoading,
    isSaving,
    error,
    saveError,
    reload: load,
    save,
  };
}
